/**
 * Bounded cursor drain for roster-style reads. A cap is never a silent
 * truncation: callers receive an explicit error when an endpoint keeps
 * returning cursors beyond the safety budget.
 */
export const QUERY_PAGE_DRAIN_MAX_PAGES = 100;

export class DrainPageBudgetError extends Error {
  constructor(maxPages: number) {
    super(`Roster pagination stopped after ${maxPages} pages.`);
    this.name = "DrainPageBudgetError";
  }
}

/** A partial roster must never be presented as an empty or complete list. */
export function shouldThrowDrainBudgetError(error: Error): boolean {
  return error instanceof DrainPageBudgetError;
}

export interface CursorPage<T> {
  readonly items: readonly T[];
  /**
   * `null` or a missing field both mean "last page". Treating only `null`
   * as the end re-requested page one until the budget ran out whenever a
   * response omitted the field.
   */
  readonly nextCursor?: string | null;
}

export async function drainCursorPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  maxPages = QUERY_PAGE_DRAIN_MAX_PAGES,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page += 1) {
    const result = await fetchPage(cursor);
    items.push(...result.items);
    if (result.nextCursor == null) return items;
    cursor = result.nextCursor;
  }
  throw new DrainPageBudgetError(maxPages);
}

/**
 * Drains a cursor feed up to a bounded number of pages. Unlike roster reads,
 * callers that can safely render a partial result receive the accumulated
 * items and an explicit truncation signal instead of an exception.
 */
export async function drainCursorPagesBounded<T>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  maxPages: number,
): Promise<{ items: T[]; truncated: boolean; lastPage: CursorPage<T> }> {
  if (maxPages < 1) {
    throw new Error("Bounded pagination requires at least one page.");
  }

  const items: T[] = [];
  let cursor: string | undefined;
  let lastPage: CursorPage<T> | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const result = await fetchPage(cursor);
    lastPage = result;
    items.push(...result.items);
    if (result.nextCursor == null) {
      return { items, truncated: false, lastPage: result };
    }
    cursor = result.nextCursor;
  }

  // `maxPages >= 1`, so the loop always assigned this before reaching here.
  return { items, truncated: true, lastPage: lastPage! };
}
